import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, Radio } from 'lucide-react';
import type { OnuTrafficSample, OnuTrafficSeries } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { Button, Card, Spinner } from '../../components/ui';
import { RefreshButton } from '../../components/RefreshButton';
import {
  EASE_MS,
  easeOut,
  newestPoint,
  trafficCurve,
  tweenSeries,
  type CurvePoint,
} from '../../lib/traffic-curve';
import { liveRefusalNotice } from '../../lib/traffic-refusal';
import { clockLabel, compactBitRate, rateTicks, timeTicks } from '../../lib/traffic-axes';

/**
 * How much this subscriber is actually pushing and pulling — the last three
 * hours by default, or a live meter while somebody is watching one come up.
 *
 * Download leads because it is the number a subscriber calls about — the plan
 * they pay for is the down figure — and upload sits beside it in its own hue.
 * The two are read together against the same scale, so a link pinned at its
 * ceiling and one barely ticking over look as different as they are.
 *
 * Two cadences:
 *  · STATIC (default) shows the stored series over the last three hours and
 *    tops it up every thirty seconds while the tab is in front of somebody.
 *  · LIVE, behind a button, reads back-to-back — a pause of LIVE_POLL_MS after
 *    each read SETTLES, never a fixed interval that could queue a second read
 *    behind a slow one — and shows a short rolling window, for watching a line
 *    settle in real time. It reads the chassis interface the same way the
 *    static sampler does — on the ZTE C300 the per-ONU rate is a CLI read
 *    (`show interface`), because the chassis exposes no per-ONU byte counter
 *    over SNMP (the realtime and OMCI PM tables both come back empty on this
 *    fleet), and that CLI read is the only source that gives BOTH directions.
 *    The chassis reports its own averaged rate, so consecutive reads change
 *    smoothly and a faster cadence draws a truer line, not a noisier one. Live
 *    mode is still bounded: it stops itself after a few minutes and never runs
 *    on a hidden tab, so a page left open cannot hold a chassis session busy
 *    for an answer nobody is watching.
 *
 * One reading is in flight at a time (a ref gate), so the static interval, the
 * live loop, the resume-on-visible and the manual button can never stack two
 * reads onto the same chassis at once — and a live sample yields when the OLT
 * is busy with an operator's own work (the server skips it rather than
 * queueing, and answers with the series unchanged plus a refusal sentence, so
 * a refused tick adds no point: the trace glides on with a gap in its cadence,
 * never a false zero). That sentence is shown under the trace as the server
 * wrote it — the busy skip is the only one that earns "picks up when the
 * chassis is free"; an offline unit or a failed read says what it is
 * (`lib/traffic-refusal.ts`).
 *
 * The trace is a monotone curve, not a polyline, and in LIVE mode it glides:
 * the x-axis is anchored to the clock and advanced every frame, and a fresh
 * reading eases into the line rather than snapping it — the operator's word
 * for the first cut was "jerky", in look and in motion. `Sparkline` below.
 */

/** Download blue is the app's own primary; upload takes a teal the semantic
 *  warn/danger palette does not already own, so an amber line never reads as a
 *  problem on a card where amber means one everywhere else. */
const DOWN_COLOR = 'var(--primary)';
const UP_COLOR = '#14b8a6';

/** Static view: the last three hours of the stored series. */
const STATIC_HOURS = 3;
/** Static top-up cadence while the tab is visible. */
const STATIC_POLL_MS = 30_000;
/**
 * Live cadence: the pause between one read settling and the next starting.
 * Not a period — a slow CLI answer simply pushes the next read later, so two
 * can never be queued against the chassis. 3 s became 1.5 s, and 300 ms once
 * the live read stopped opening a telnet session per sample (2026-09-18: the
 * platform holds one CLI session per chassis while a meter runs, so a read is
 * a single command round trip — measured ~1.0 s on the Lagos C650, the
 * chassis's own prompt latency — giving readings ~1.3 s apart). The rate is
 * the byte delta over the interval, so a shorter, steadier interval is a truer
 * trace, not a noisier one.
 */
const LIVE_POLL_MS = 300;
/**
 * The rolling window LIVE draws, anchored to now and sliding continuously.
 * Sixty seconds: at the ~1.3 s live cadence that is ~46 readings across the
 * card, sixteen pixels apart, so a one-sample burst is a rounded hill and a
 * few seconds of load is a mountain — three minutes drew both as needles.
 */
const LIVE_WINDOW_MS = 60_000;
/** The ceiling LIVE stops itself at, watched or not. */
const LIVE_MAX_MS = 5 * 60_000;

/** One stable empty series, so "no data yet" is not a new array on every render. */
const NO_SAMPLES: OnuTrafficSample[] = [];

/**
 * A byte-per-second rate as the bit-rate a human reads on a plan: bits, not
 * bytes (multiply by eight), stepping 1000 to the unit. A gap — the chassis
 * gave no figure — is a dash, not a zero.
 */
function formatBps(bytesPerSecond: number | null | undefined): string {
  if (bytesPerSecond == null || !Number.isFinite(bytesPerSecond) || bytesPerSecond < 0) return '—';
  const units = ['bit/s', 'kbit/s', 'Mbit/s', 'Gbit/s'] as const;
  let value = bytesPerSecond * 8;
  let i = 0;
  while (value >= 1000 && i < units.length - 1) {
    value /= 1000;
    i += 1;
  }
  const unit = units[i] ?? 'bit/s';
  // Whole bits get no decimals; a scaled unit keeps enough to tell 12.3 from 12.
  const digits = i === 0 ? 0 : value < 10 ? 2 : value < 100 ? 1 : 0;
  return `${value.toFixed(digits)} ${unit}`;
}

export function TrafficCard({
  oltId,
  onuId,
  vendor,
}: {
  oltId: string;
  onuId: string;
  vendor: string;
}) {
  const queryClient = useQueryClient();
  const base = `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}`;

  const query = useQuery({
    queryKey: ['onu-traffic', onuId],
    queryFn: () => api.get<OnuTrafficSeries>(`${base}/traffic?hours=${STATIC_HOURS}`),
  });

  const [live, setLive] = useState(false);

  /**
   * One sample at a time, guarded synchronously in a ref so two reads never
   * overlap even if an interval fires while the manual button's own read is
   * still out. The state beside it is only for the pulsing header icon — the
   * ref is what actually holds the gate.
   */
  const inFlight = useRef(false);
  const [sampling, setSampling] = useState(false);
  /** Whether the live loop is the caller — a live read rides the chassis's held session. */
  const liveRef = useRef(false);
  useEffect(() => {
    liveRef.current = live;
  }, [live]);

  const sampleOnce = useCallback(async (): Promise<OnuTrafficSeries | null> => {
    if (inFlight.current) return null;
    inFlight.current = true;
    setSampling(true);
    try {
      // One call both samples the chassis and returns the whole refreshed
      // series, so the cache is updated from the same round-trip.
      const series = await api.post<OnuTrafficSeries>(`${base}/traffic/sample`, { live: liveRef.current });
      queryClient.setQueryData(['onu-traffic', onuId], series);
      return series;
    } finally {
      inFlight.current = false;
      setSampling(false);
    }
  }, [base, onuId, queryClient]);

  // STATIC top-up: sample on mount, then every 30s — but only while somebody is
  // looking, and never while LIVE is running its own faster loop. Torn down
  // when the tab is hidden and started again (with an immediate read) when it
  // returns to the front, so a tab picked back up is fresh.
  useEffect(() => {
    if (live) return;
    let timer: ReturnType<typeof setInterval> | undefined;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      void sampleOnce();
    };
    const start = () => {
      if (timer !== undefined) return;
      tick();
      timer = setInterval(tick, STATIC_POLL_MS);
    };
    const stop = () => {
      if (timer !== undefined) {
        clearInterval(timer);
        timer = undefined;
      }
    };
    const onVisibility = () => (document.visibilityState === 'visible' ? start() : stop());
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [sampleOnce, live]);

  // LIVE: while on and visible, read, wait LIVE_POLL_MS, read again — chained
  // with a timeout armed only after the previous read has settled, so the
  // cadence is "as fast as the chassis answers plus a beat" and a slow answer
  // never has a second read stacked behind it (an interval would keep firing
  // into the in-flight gate and the trace would come in bursts). Stops after
  // the ceiling. Hiding the tab stops it at once, not at the next tick; coming
  // back does not silently resume a chassis-heavy loop — the operator turns
  // it back on. A read that fails (the API away for a moment) is a missed
  // beat, not the end of the meter: the deadline is what ends it.
  useEffect(() => {
    if (!live) return;
    const deadline = Date.now() + LIVE_MAX_MS;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    const loop = async () => {
      if (stopped) return;
      if (Date.now() >= deadline) {
        setLive(false);
        return;
      }
      if (document.visibilityState !== 'visible') {
        setLive(false); // a hidden tab has no watcher; drop the fast loop entirely
        return;
      }
      try {
        await sampleOnce();
      } catch {
        // A failed read has already released the gate; the next beat retries.
      }
      if (stopped) return;
      timer = setTimeout(() => void loop(), LIVE_POLL_MS);
    };
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') setLive(false);
    };
    document.addEventListener('visibilitychange', onVisibility);
    void loop();
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [live, sampleOnce]);

  /**
   * The manual sample's own answer, shown briefly under the figures. The card
   * already updates its numbers when a reading lands, so this exists mostly for
   * the reading that was refused — the chassis was busy, nothing changed, and
   * without a word the button would look like it did nothing.
   */
  const [flash, setFlash] = useState<{ text: string; tone: 'info' | 'danger' } | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const notice = useCallback((text: string, tone: 'info' | 'danger' = 'info') => {
    setFlash({ text, tone });
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 4000);
  }, []);
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    [],
  );

  const series = query.data;
  const all = series?.samples ?? NO_SAMPLES;
  // Live draws a short rolling window; static draws the three hours the label
  // promises. Both are cut here because a sample's answer carries the WHOLE
  // stored series (a week), and the static graph once drew all of it under a
  // "last 3 hours" caption after the first top-up. The live cut keeps a minute
  // of readings past the window's head (room for a server clock a little
  // behind this one) so the curve runs off the left edge instead of starting
  // inside it — the sparkline trims to the exact, sliding window every frame.
  const samples = useMemo(() => {
    const windowStart = live
      ? Date.now() - LIVE_WINDOW_MS - 60_000
      : Date.now() - STATIC_HOURS * 3_600_000;
    return all.filter((s) => s.at >= windowStart);
  }, [all, live]);
  const latest = all.length ? all[all.length - 1] : undefined;
  // Only LIVE shows the latest tick's refusal under the trace; the manual
  // button's flash already carries it in static mode.
  const liveNotice = live ? liveRefusalNotice(series?.refusal) : null;
  const vendorLabel = vendor === 'huawei' ? 'Huawei' : vendor === 'zte' ? 'ZTE' : vendor;

  const title = (
    <span className="flex items-center gap-2">
      <Activity className={`size-4 ${sampling ? 'animate-pulse text-primary' : 'text-muted'}`} />
      Traffic
      {live && (
        <span className="flex items-center gap-1 rounded-full bg-danger/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-danger">
          <span className="size-1.5 animate-pulse rounded-full bg-danger" />
          Live
        </span>
      )}
    </span>
  );

  const action = (
    <span className="flex items-center gap-2">
      <Button
        size="sm"
        variant={live ? 'primary' : 'ghost'}
        icon={<Radio className="size-3.5" />}
        onClick={() => setLive((on) => !on)}
        title={
          live
            ? 'Stop the live meter'
            : 'Read the WAN throughput live — a fast poll of the chassis interface, both directions, for a few minutes'
        }
      >
        {live ? 'Stop' : 'Live'}
      </Button>
      {!live && (
        <RefreshButton
          label="the throughput"
          run={async () => {
            const fresh = await sampleOnce();
            if (!fresh) return { message: 'A reading is already being taken — one moment.' };
            if (fresh.refusal) return { message: fresh.refusal };
            const now = fresh.samples.length ? fresh.samples[fresh.samples.length - 1] : undefined;
            return {
              message:
                now && now.downBps != null ? `Sampled — ${formatBps(now.downBps)} down` : 'Sampled.',
            };
          }}
          invalidate={[['onu-traffic', onuId]]}
          onNotice={notice}
        />
      )}
    </span>
  );

  let body: ReactNode;
  if (!series) {
    body = query.isError ? (
      <div className="px-5 py-6 text-center text-sm text-muted">
        Could not read throughput from the chassis.
      </div>
    ) : (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  } else if (all.length === 0) {
    // Nothing recorded yet. If the chassis said why it will not read this unit,
    // show that quietly; otherwise the sampler is simply still collecting.
    body = (
      <div className="px-5 py-6 text-center text-sm text-muted">
        {series.refusal ??
          (live ? 'Waiting for the first live reading…' : 'No throughput sampled yet — collecting…')}
      </div>
    );
  } else {
    body = (
      <div>
        <div className="grid grid-cols-2 divide-x divide-line">
          <Current label="Download" color={DOWN_COLOR} bps={latest?.downBps ?? null} />
          <Current label="Upload" color={UP_COLOR} bps={latest?.upBps ?? null} />
        </div>
        {/* One point is a figure, not a line — the sparkline waits for a second
            reading before it has anything to draw. */}
        {samples.length >= 2 ? (
          <div className="border-t border-line px-4 py-2">
            <Sparkline samples={samples} live={live} />
            <p className="mt-1 text-center text-[10px] uppercase tracking-wide text-faint">
              {live ? 'Last minute, live' : `Last ${STATIC_HOURS} hours`}
            </p>
          </div>
        ) : (
          live && (
            <div className="border-t border-line px-5 py-3 text-center text-xs text-muted">
              Live — building the trace…
            </div>
          )
        )}
        {/* A live tick that brought no reading adds no point, so the trace
            simply keeps gliding — which, without a word, reads as the meter
            having stalled. Say why, quietly, in the server's own sentence: the
            "picks up when the chassis is free" promise follows ONLY the lock
            skip (`lib/traffic-refusal.ts`). It once followed every refusal, so
            an ONU that dropped mid-meter read "…it is probably offline. The
            meter picks up when the chassis is free." — the chassis was free;
            the unit was gone. */}
        {liveNotice && (
          <p className="border-t border-line px-5 py-1.5 text-[11px] text-faint">{liveNotice}</p>
        )}
        {series.source === 'snmp' && (
          <p className="border-t border-line px-5 py-1.5 text-[11px] text-faint">
            {vendorLabel} rates are composed from the MIB and unverified.
          </p>
        )}
        {flash && (
          <p
            className={`border-t border-line px-5 py-1.5 text-xs ${
              flash.tone === 'danger' ? 'text-danger' : 'text-muted'
            }`}
          >
            {flash.text}
          </p>
        )}
      </div>
    );
  }

  return (
    <Card title={title} action={action}>
      {body}
    </Card>
  );
}

/** A single current rate, with the coloured dot that ties it to its line below. */
function Current({ label, color, bps }: { label: string; color: string; bps: number | null }) {
  return (
    <div className="px-5 py-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-faint">
        <span className="size-2 shrink-0 rounded-full" style={{ background: color }} />
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-ink">{formatBps(bps)}</div>
    </div>
  );
}

/**
 * Whether the person watching asked their OS for less motion. Read once and
 * then followed, so a setting changed mid-session is honoured without a
 * reload.
 */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/** One series in the data domain: x is the sample's time, y its rate — or a gap. */
type Trace = Array<CurvePoint | null>;

/** How fast the scale settles after the tallest reading leaves the window. */
const PEAK_EASE_MS = 300;
/** The longest a reading takes to glide in: past this a slow chassis is a step, not a crawl. */
const MAX_EASE_MS = 2_500;

const H = 118;
/** The gutters: rate labels down the left, the clock along the bottom (operator, 2026-09-13). */
const PAD_L = 48;
const PAD_R = 8;
const PAD_TOP = 10;
const PAD_BOTTOM = 22;
/** y(0): the zero line never moves whatever the scale, so it is drawn once. */
const BASE_Y = H - PAD_BOTTOM;
/** How many gridlines and clock marks the SVG holds nodes for; paint() hides the spare ones. */
const MAX_RATE_TICKS = 3;
const MAX_TIME_TICKS = 9;
const DOT_R = 3;

/**
 * Download and upload over the window, two curves on one scale.
 *
 * Width is measured rather than scaled, so the strokes stay a real two pixels
 * however wide the card is drawn — a fixed viewBox stretched to fit would thin
 * the lines to a hair on a wide screen. Time is the x-axis, not the sample's
 * index, so a stretch where the sampler was paused (a hidden tab) reads as the
 * gap it was rather than being squeezed shut. A null figure breaks its curve:
 * the pen lifts and comes back down at the next real reading.
 *
 * Each series is a monotone cubic through its samples (`trafficCurve`), so an
 * idle link is a flat line ON the baseline and a spike is as tall as the
 * chassis said and no taller. What makes a burst read as a rounded hill
 * rather than a needle is the live window: sixty seconds at a ~1.3 s cadence
 * puts sixteen pixels between readings (operator, 2026-09-18: "curvy smoother
 * curves not sharp points" — a three-minute window drew every burst five
 * pixels wide whatever the spline). Download carries a soft fill under it and an
 * end-dot; upload is a line with its own dot, so the two stay distinguishable
 * where they cross.
 *
 * Geometry is painted straight onto the SVG nodes, not rendered through React
 * state: in LIVE mode every frame moves the trace, and re-rendering the card
 * sixty times a second for two `d` attributes is work with nothing to show
 * for it. React owns the elements; `paint` owns where the ink goes. Static
 * mode paints once per reading (and per resize) and runs no animation loop.
 *
 * LIVE anchors the right edge to NOW and draws the LIVE_WINDOW_MS before it,
 * advanced by requestAnimationFrame while the tab is visible, so the trace
 * glides left between readings instead of jumping when one lands. NOW is the
 * browser's clock corrected by the smallest offset seen between a reading's
 * stamp and its arrival — a chassis whose clock runs ahead cannot push its
 * newest point past the right edge, and one running behind does not leave it
 * hanging short. A landed reading eases into the line over EASE_MS from the
 * previous newest reading (`tweenSeries`), and the scale eases DOWN when the
 * tallest reading leaves the window (rising is immediate, with the point that
 * raised it, so nothing is ever drawn above the top). A watcher who asked for
 * reduced motion gets none of the gliding: the window is drawn where the
 * clock stands when a reading lands, and that is all.
 */
function Sparkline({ samples, live }: { samples: OnuTrafficSample[]; live: boolean }) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [W, setW] = useState(480);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setW(Math.max(200, Math.floor(el.clientWidth)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const reducedMotion = usePrefersReducedMotion();
  // React 19's ids carry guillemets (`«r1»`); a `url(#…)` reference wants
  // something plainer, and the stripped form is still unique on the page.
  const gradientId = `traffic-fill-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`;

  const downAreaRef = useRef<SVGPathElement | null>(null);
  const downLineRef = useRef<SVGPathElement | null>(null);
  const upLineRef = useRef<SVGPathElement | null>(null);
  const downDotRef = useRef<SVGCircleElement | null>(null);
  const upDotRef = useRef<SVGCircleElement | null>(null);
  // The axes: a fixed set of gridline + label nodes, positioned by paint()
  // and hidden when a frame needs fewer — no React work per frame.
  const rateLineRefs = useRef<Array<SVGLineElement | null>>([]);
  const rateTextRefs = useRef<Array<SVGTextElement | null>>([]);
  const timeLineRefs = useRef<Array<SVGLineElement | null>>([]);
  const timeTextRefs = useRef<Array<SVGTextElement | null>>([]);

  // Where the trace is heading (the readings as they stand), what it shows
  // this frame, and the tween carrying it from the one to the other.
  const target = useMemo<{ down: Trace; up: Trace }>(
    () => ({
      down: samples.map((s) => (s.downBps == null ? null : { x: s.at, y: s.downBps })),
      up: samples.map((s) => (s.upBps == null ? null : { x: s.at, y: s.upBps })),
    }),
    [samples],
  );
  const shownRef = useRef<{ down: Trace; up: Trace }>({ down: [], up: [] });
  const tweenRef = useRef<{ from: { down: Trace; up: Trace }; startedAt: number; durationMs: number } | null>(null);
  const landedAtRef = useRef<number | null>(null);
  /** The scale as drawn — eased, so it is not always the window's true peak. */
  const peakRef = useRef<number | null>(null);
  const lastPaintRef = useRef(0);
  /** When the previous reading landed (performance clock), for the adaptive ease. */
  const landedPerfRef = useRef<number | null>(null);
  /** Smallest (arrival − stamp) seen for a reading that landed while live: the clock offset, near enough. */
  const skewRef = useRef(Number.POSITIVE_INFINITY);

  const paint = (now: number) => {
    const plotW = Math.max(1, W - PAD_L - PAD_R);

    // 1. The shape this frame: partway through the tween, or the target
    //    itself. Only the live loop can finish a tween — static mode paints
    //    once per reading with nothing to carry a half-drawn frame forward —
    //    so outside live the target is drawn outright and any tween dropped.
    if (!live || reducedMotion) tweenRef.current = null;
    const tween = tweenRef.current;
    let e = 1;
    if (tween) {
      const t = (now - tween.startedAt) / tween.durationMs;
      if (t >= 1) tweenRef.current = null;
      else e = easeOut(t);
    }
    const shown =
      e < 1 && tween
        ? { down: tweenSeries(tween.from.down, target.down, e), up: tweenSeries(tween.from.up, target.up, e) }
        : target;
    shownRef.current = shown;

    // 2. The window in time. Live: the last LIVE_WINDOW_MS up to corrected
    //    now. Static: the readings' own span, edge to edge.
    let t0: number;
    let t1: number;
    if (live) {
      const newestAt = samples.length ? samples[samples.length - 1]!.at : Date.now();
      t1 = Number.isFinite(skewRef.current) ? Date.now() - skewRef.current : Math.max(Date.now(), newestAt);
      t0 = t1 - LIVE_WINDOW_MS;
    } else {
      t0 = Number.POSITIVE_INFINITY;
      t1 = Number.NEGATIVE_INFINITY;
      for (const s of samples) {
        if (s.at < t0) t0 = s.at;
        if (s.at > t1) t1 = s.at;
      }
      if (!Number.isFinite(t0)) t0 = t1 = Date.now();
    }
    const tSpan = Math.max(t1 - t0, 1);

    // 3. What is in view: everything from the window's head on, plus the one
    //    reading before it so the curve enters from off the left edge (the
    //    SVG clips it) rather than starting inside the frame.
    let start = 0;
    if (live) {
      let i = 0;
      while (i < samples.length && samples[i]!.at < t0) i += 1;
      start = Math.max(0, i - 1);
    }
    const downIn = shown.down.slice(start);
    const upIn = shown.up.slice(start);

    // 4. The scale. At least 1, so an idle link (all zeros) sits on the
    //    baseline instead of dividing by nought. Rising follows the reading
    //    that raised it at once; falling eases, so a spike leaving the window
    //    lets the picture relax rather than snap.
    let targetPeak = 1;
    for (const p of downIn) if (p && p.y > targetPeak) targetPeak = p.y;
    for (const p of upIn) if (p && p.y > targetPeak) targetPeak = p.y;
    let peak = peakRef.current;
    if (peak == null || !live || reducedMotion || targetPeak >= peak) peak = targetPeak;
    else {
      const dt = Math.max(0, now - lastPaintRef.current);
      peak += (targetPeak - peak) * (1 - Math.exp(-dt / PEAK_EASE_MS));
      if (peak - targetPeak < targetPeak * 1e-3) peak = targetPeak;
    }
    peakRef.current = peak;
    lastPaintRef.current = now;

    // 5. Ink.
    const x = (at: number) => PAD_L + ((at - t0) / tSpan) * plotW;
    const y = (bps: number) => PAD_TOP + (1 - bps / peak) * (H - PAD_TOP - PAD_BOTTOM);
    const toPx = (trace: Trace): Trace => trace.map((p) => (p ? { x: x(p.x), y: y(p.y) } : null));
    const downPx = toPx(downIn);
    const upPx = toPx(upIn);
    const down = trafficCurve(downPx, BASE_Y);
    const up = trafficCurve(upPx, BASE_Y);
    downAreaRef.current?.setAttribute('d', down.area);
    downLineRef.current?.setAttribute('d', down.line);
    upLineRef.current?.setAttribute('d', up.line);
    const placeDot = (el: SVGCircleElement | null, p: CurvePoint | null) => {
      if (!el) return;
      if (!p) {
        el.setAttribute('r', '0');
        return;
      }
      el.setAttribute('cx', p.x.toFixed(2));
      el.setAttribute('cy', p.y.toFixed(2));
      el.setAttribute('r', String(DOT_R));
    };
    placeDot(downDotRef.current, newestPoint(downPx));
    placeDot(upDotRef.current, newestPoint(upPx));

    // 6. The axes. Rate gridlines at round bit rates inside the eased
    //    scale, labelled in the left gutter; clock marks at round times
    //    inside the window, labelled under the baseline — in live mode they
    //    slide with the trace, since the window is anchored to now.
    const rates = rateTicks(peak, MAX_RATE_TICKS);
    for (let i = 0; i < MAX_RATE_TICKS; i += 1) {
      const line = rateLineRefs.current[i];
      const text = rateTextRefs.current[i];
      const v = rates[i];
      if (v === undefined) {
        line?.setAttribute('visibility', 'hidden');
        text?.setAttribute('visibility', 'hidden');
        continue;
      }
      const yy = y(v).toFixed(2);
      if (line) {
        line.setAttribute('y1', yy);
        line.setAttribute('y2', yy);
        line.setAttribute('x1', String(PAD_L));
        line.setAttribute('x2', String(W - PAD_R));
        line.setAttribute('visibility', 'visible');
      }
      if (text) {
        text.setAttribute('y', yy);
        text.setAttribute('x', String(PAD_L - 6));
        text.textContent = compactBitRate(v);
        text.setAttribute('visibility', 'visible');
      }
    }
    const clock = timeTicks(t0, t1);
    for (let i = 0; i < MAX_TIME_TICKS; i += 1) {
      const line = timeLineRefs.current[i];
      const text = timeTextRefs.current[i];
      const at = clock.at[i];
      // A mark within a label's width of either edge would be cut off or
      // collide with the rate labels: skipped, the rest stay where they are.
      const xx = at === undefined ? Number.NaN : x(at);
      if (at === undefined || xx < PAD_L + 18 || xx > W - PAD_R - 18) {
        line?.setAttribute('visibility', 'hidden');
        text?.setAttribute('visibility', 'hidden');
        continue;
      }
      const xs = xx.toFixed(2);
      if (line) {
        line.setAttribute('x1', xs);
        line.setAttribute('x2', xs);
        line.setAttribute('y1', String(BASE_Y));
        line.setAttribute('y2', String(BASE_Y + 4));
        line.setAttribute('visibility', 'visible');
      }
      if (text) {
        text.setAttribute('x', xs);
        text.setAttribute('y', String(BASE_Y + 15));
        text.textContent = clockLabel(at, clock.withSeconds);
        text.setAttribute('visibility', 'visible');
      }
    }
  };
  // The loop calls whatever the latest render's paint is, so a resize or a
  // fresh reading reaches it without restarting the animation.
  const paintRef = useRef(paint);
  paintRef.current = paint;

  // A reading landed: start easing from what is shown to what is now true,
  // and note the clock offset. Then, unless the live loop is painting on its
  // own, paint now — before the browser shows the frame, so a resize or the
  // first render never flashes an empty chart.
  useLayoutEffect(() => {
    const newest = samples.length ? samples[samples.length - 1]!.at : null;
    if (newest !== null && newest !== landedAtRef.current) {
      // The reading the card mounted with may be minutes old: only one that
      // landed while we were watching starts a tween or says anything about
      // the clock.
      const first = landedAtRef.current === null;
      landedAtRef.current = newest;
      if (!first && live && !reducedMotion) {
        // Ease over the interval readings are actually arriving at (clamped),
        // so one reading glides into the next just as the next lands — a
        // fixed 450 ms against a 2 s cadence was a step and a wait.
        const nowMs = performance.now();
        const interval = landedPerfRef.current === null ? EASE_MS : nowMs - landedPerfRef.current;
        landedPerfRef.current = nowMs;
        tweenRef.current = {
          from: shownRef.current,
          startedAt: nowMs,
          durationMs: Math.min(MAX_EASE_MS, Math.max(EASE_MS, interval)),
        };
      } else if (!first) {
        landedPerfRef.current = performance.now();
      }
      if (!first && live) skewRef.current = Math.min(skewRef.current, Date.now() - newest);
    }
    if (!live || reducedMotion) paintRef.current(performance.now());
  }, [samples, live, W, reducedMotion]);

  // LIVE glide: a frame loop, only while live, only for a watcher who has not
  // asked for reduced motion, painting only while the tab is visible (a
  // hidden tab's frames are throttled anyway, and the card stops live mode on
  // hide); cancelled on cleanup so nothing keeps running after Stop.
  useEffect(() => {
    if (!live || reducedMotion) return;
    let raf = 0;
    const frame = (now: number) => {
      if (document.visibilityState === 'visible') paintRef.current(now);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [live, reducedMotion]);

  return (
    <div ref={wrapRef} className="w-full">
      <svg
        width={W}
        height={H}
        className="block"
        role="img"
        aria-label="Download and upload throughput"
      >
        <defs>
          {/* The fill fades from the download blue to nothing at the zero
              line; `stopColor` goes through style so the theme variable
              resolves — a presentation attribute would not take a var(). */}
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" style={{ stopColor: DOWN_COLOR, stopOpacity: 0.18 }} />
            <stop offset="1" style={{ stopColor: DOWN_COLOR, stopOpacity: 0 }} />
          </linearGradient>
        </defs>
        {/* The rate gridlines and their labels — positioned by paint(). The
            colours are the theme's own variables (`--border`, `--text-faint`):
            an SVG attribute cannot read a Tailwind colour name. */}
        {Array.from({ length: MAX_RATE_TICKS }, (_, i) => (
          <line
            key={`r${i}`}
            ref={(el) => {
              rateLineRefs.current[i] = el;
            }}
            visibility="hidden"
            stroke="var(--border)"
            strokeWidth={1}
            strokeDasharray="2 3"
          />
        ))}
        {Array.from({ length: MAX_RATE_TICKS }, (_, i) => (
          <text
            key={`rt${i}`}
            ref={(el) => {
              rateTextRefs.current[i] = el;
            }}
            visibility="hidden"
            textAnchor="end"
            dominantBaseline="middle"
            fontSize={10}
            fill="var(--text-faint)"
            className="tabular-nums"
          />
        ))}
        {/* The faint zero line the two rates ride on, and its label. */}
        <line x1={PAD_L} x2={W - PAD_R} y1={BASE_Y} y2={BASE_Y} stroke="var(--border)" strokeWidth={1} />
        <text x={PAD_L - 6} y={BASE_Y} textAnchor="end" dominantBaseline="middle" fontSize={10} fill="var(--text-faint)">
          0
        </text>
        {/* The clock marks under the baseline — positioned by paint(). */}
        {Array.from({ length: MAX_TIME_TICKS }, (_, i) => (
          <line
            key={`t${i}`}
            ref={(el) => {
              timeLineRefs.current[i] = el;
            }}
            visibility="hidden"
            stroke="var(--border)"
            strokeWidth={1}
          />
        ))}
        {Array.from({ length: MAX_TIME_TICKS }, (_, i) => (
          <text
            key={`tt${i}`}
            ref={(el) => {
              timeTextRefs.current[i] = el;
            }}
            visibility="hidden"
            textAnchor="middle"
            fontSize={10}
            fill="var(--text-faint)"
            className="tabular-nums"
          />
        ))}
        <path ref={downAreaRef} fill={`url(#${gradientId})`} stroke="none" />
        <path
          ref={upLineRef}
          fill="none"
          stroke={UP_COLOR}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <path
          ref={downLineRef}
          fill="none"
          stroke={DOWN_COLOR}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* End-dots on the newest reading of each series — where "now" is. */}
        <circle ref={upDotRef} r={0} fill={UP_COLOR} />
        <circle ref={downDotRef} r={0} fill={DOWN_COLOR} />
      </svg>
    </div>
  );
}
